/**
 * Zonas de lanzamiento: cuadrícula 3×3 de la zona de strike rodeada de un
 * anillo de 8 zonas de bola. El lanzador elige la zona objetivo; el servidor
 * decide (con su propio azar con semilla) en qué zona cae de verdad según la
 * precisión del tipo de lanzamiento. Documentado en `docs/ZONAS_Y_BATEO.md`.
 *
 * Geometría en el home (m), vista desde el receptor: x hacia la derecha de la
 * pantalla, y hacia arriba. El bateador diestro está en x < 0, así que para
 * él la columna izquierda (-x) es «adentro».
 *
 *   13 |  9  9  9 | 14        ← arriba (bola)
 *   11 |  0  1  2 | 12
 *   11 |  3  4  5 | 12        ← zona de strike 0..8
 *   11 |  6  7  8 | 12
 *   15 | 10 10 10 | 16        ← abajo (bola)
 */

export type PitchZone = number;

/** Bordes de las 5 columnas (x) y las 5 filas (y, de abajo arriba) de la cuadrícula. */
const W = 0.4; // = STRIKE_ZONE.halfWidth
const LOW = 0.45; // = STRIKE_ZONE.low
const HIGH = 1.15; // = STRIKE_ZONE.high
const COL_EDGES = [-0.75, -W, -W / 3, W / 3, W, 0.75];
const ROW_EDGES = [0.12, LOW, LOW + (HIGH - LOW) / 3, LOW + (2 * (HIGH - LOW)) / 3, HIGH, 1.48];

interface ZoneDef {
  id: PitchZone;
  /** Columnas y filas (0..4) que ocupa en la cuadrícula 5×5 (fila 0 = abajo). */
  c0: number;
  c1: number;
  r0: number;
  r1: number;
  strike: boolean;
}

const strikeCell = (id: number): ZoneDef => {
  const col = (id % 3) + 1;
  const row = 3 - Math.floor(id / 3); // id 0..2 arriba (fila 3)
  return { id, c0: col, c1: col, r0: row, r1: row, strike: true };
};

export const PITCH_ZONES: readonly ZoneDef[] = Object.freeze([
  ...Array.from({ length: 9 }, (_, i) => strikeCell(i)),
  { id: 9, c0: 1, c1: 3, r0: 4, r1: 4, strike: false }, // arriba
  { id: 10, c0: 1, c1: 3, r0: 0, r1: 0, strike: false }, // abajo
  { id: 11, c0: 0, c1: 0, r0: 1, r1: 3, strike: false }, // lado izquierdo (adentro al diestro)
  { id: 12, c0: 4, c1: 4, r0: 1, r1: 3, strike: false }, // lado derecho (afuera al diestro)
  { id: 13, c0: 0, c1: 0, r0: 4, r1: 4, strike: false }, // esquinas
  { id: 14, c0: 4, c1: 4, r0: 4, r1: 4, strike: false },
  { id: 15, c0: 0, c1: 0, r0: 0, r1: 0, strike: false },
  { id: 16, c0: 4, c1: 4, r0: 0, r1: 0, strike: false },
]);

export const ZONE_COUNT = PITCH_ZONES.length;
export const CENTER_ZONE: PitchZone = 4;

export const isPitchZone = (v: unknown): v is PitchZone => Number.isInteger(v) && (v as number) >= 0 && (v as number) < ZONE_COUNT;
export const isStrikeZone = (z: PitchZone): boolean => z >= 0 && z < 9;

/** Zona que ocupa la celda (col, row) de la cuadrícula 5×5 (fila 0 = abajo). */
export const zoneAtCell = (col: number, row: number): PitchZone =>
  PITCH_ZONES.find((z) => col >= z.c0 && col <= z.c1 && row >= z.r0 && row <= z.r1)!.id;

/** Rectángulo de una zona en el home (m). */
export const zoneRect = (z: PitchZone): { x0: number; x1: number; y0: number; y1: number } => {
  const d = PITCH_ZONES[z]!;
  return { x0: COL_EDGES[d.c0]!, x1: COL_EDGES[d.c1 + 1]!, y0: ROW_EDGES[d.r0]!, y1: ROW_EDGES[d.r1 + 1]! };
};

/** Centro de una zona en el home (m): adonde apunta el guante del receptor en su seña. */
export const zoneCenter = (z: PitchZone): { x: number; y: number } => {
  const r = zoneRect(z);
  return { x: (r.x0 + r.x1) / 2, y: (r.y0 + r.y1) / 2 };
};

const indexIn = (edges: number[], v: number): number => {
  for (let i = 1; i < edges.length - 1; i++) if (v < edges[i]!) return i - 1;
  return edges.length - 2;
};

/** Zona donde cae un punto del home (cualquier punto: lo de fuera va al anillo). */
export const zoneAt = (x: number, y: number): PitchZone => zoneAtCell(indexIn(COL_EDGES, x), indexIn(ROW_EDGES, y));

/** Zonas adyacentes (comparten lado o esquina en la cuadrícula). */
export const adjacentZones = (z: PitchZone): PitchZone[] => {
  const a = PITCH_ZONES[z]!;
  return PITCH_ZONES.filter(
    (b) => b.id !== z && b.c0 <= a.c1 + 1 && b.c1 >= a.c0 - 1 && b.r0 <= a.r1 + 1 && b.r1 >= a.r0 - 1
  ).map((b) => b.id);
};

/**
 * Precisión base por tipo: probabilidad de que la bola caiga en la zona
 * elegida (el resto, en una adyacente al azar).
 */
export const PITCH_ACCURACY: Record<string, number> = {
  fastball: 1,
  sinker: 0.75,
  changeup: 0.7,
  slider: 0.6,
  curveball: 0.5,
};

/** Generador con semilla (mulberry32): mismo resultado con la misma semilla. */
export const seededRandom = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * Zona real del lanzamiento y punto exacto de llegada dentro de ella.
 * `accuracy` ya incluye los modificadores (dificultad de la CPU, cansancio).
 */
export const resolvePitchZone = (
  target: PitchZone,
  accuracy: number,
  rng: () => number
): { zone: PitchZone; hit: boolean; x: number; y: number } => {
  const hit = rng() < accuracy;
  let zone = target;
  if (!hit) {
    const adj = adjacentZones(target);
    zone = adj[Math.floor(rng() * adj.length)] ?? target;
  }
  const r = zoneRect(zone);
  // Punto dentro de la zona (lejos de los bordes: nunca cae en la vecina).
  const x = (r.x0 + r.x1) / 2 + (rng() - 0.5) * 0.6 * (r.x1 - r.x0);
  const y = (r.y0 + r.y1) / 2 + (rng() - 0.5) * 0.6 * (r.y1 - r.y0);
  return { zone, hit, x, y };
};

/**
 * Nombre de una zona para el bateador (`side`: 1 diestro en x<0, -1 zurdo).
 * «Adentro» es el lado del bateador.
 */
export const zoneLabel = (z: PitchZone, side: 1 | -1 = 1): string => {
  if (isStrikeZone(z)) {
    const row = ['arriba', 'medio', 'abajo'][Math.floor(z / 3)]!;
    const colIdx = z % 3;
    const col = colIdx === 1 ? 'centro' : (colIdx === 0) === (side === 1) ? 'adentro' : 'afuera';
    return row === 'medio' && col === 'centro' ? 'centro' : `${row} ${col === 'centro' ? 'al centro' : col}`;
  }
  const leftIsIn = side === 1;
  const names: Record<number, string> = {
    9: 'alta',
    10: 'baja',
    11: leftIsIn ? 'pegada (adentro)' : 'afuera',
    12: leftIsIn ? 'afuera' : 'pegada (adentro)',
    13: leftIsIn ? 'alta y adentro' : 'alta y afuera',
    14: leftIsIn ? 'alta y afuera' : 'alta y adentro',
    15: leftIsIn ? 'baja y adentro' : 'baja y afuera',
    16: leftIsIn ? 'baja y afuera' : 'baja y adentro',
  };
  return `bola ${names[z]}`;
};
